import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { isPlatformBrowser } from '@angular/common';
import { getStoredAuthToken } from '../../auth/auth-token';

export interface UserListItemPayload {
  item_id: number;
  type: string;
  date_added: string;
  movie_data?: any; // Full movie/tv object from TMDB
}

export interface UserListCreatePayload {
  name: string;
  dscr: string;
  image: string;
  backdrop_image: string;
  type: string;
  items: UserListItemPayload[];
  created_at: string;
  updated_at: string;
}

export interface UserListUpdatePayload {
  name?: string;
  description?: string;
  image?: string;
  backdrop_image?: string;
  type?: string;
}

@Injectable({ providedIn: 'root' })
export class UserListService {
  private readonly platformId = inject(PLATFORM_ID);

  constructor(private http: HttpClient) {}

  getUserLists(): Observable<any> {
    return this.http.get<any>(`${environment.backendUrl}/api/user/list`, {
      headers: this.authHeaders(),
    });
  }

  createList(payload: UserListCreatePayload): Observable<any> {
    return this.http.post<any>(`${environment.backendUrl}/api/list/create`, payload, {
      headers: this.authHeaders(),
    });
  }

  addItemToList(payload: UserListItemPayload, index: number): Observable<any> {
    return this.http.post<any>(`${environment.backendUrl}/api/list/item/add/`, payload, {
      params: { index: String(index) },
      headers: this.authHeaders(),
    });
  }

  removeItemFromList(listId: string, index: number): Observable<any> {
    return this.http.delete<any>(`${environment.backendUrl}/api/list/${listId}/item/delete/`, {
      params: { index: String(index) },
      headers: this.authHeaders(),
    });
  }

  deleteList(index: number): Observable<any> {
    return this.http.delete<any>(`${environment.backendUrl}/api/list/delete`, {
      params: { index: String(index) },
      headers: this.authHeaders(),
    });
  }

  getListItems(listId: string): Observable<any> {
    return this.http.get<any>(`${environment.backendUrl}/api/list/${listId}/items`, {
      headers: this.authHeaders(),
    });
  }

  updateList(listId: string, payload: UserListUpdatePayload): Observable<any> {
    return this.http.patch<any>(`${environment.backendUrl}/api/list/${listId}/update`, payload, {
      headers: this.authHeaders(),
    });
  }

  private authHeaders(): HttpHeaders {
    const token = this.getAuthToken() ?? '';
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
  }

  private getAuthToken(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    return getStoredAuthToken();
  }
}
